import { authenticatedFetch } from '../utils/authenticatedFetch';
import { configs } from '../configs';

export interface InventoryCategory {
  id: string;
  name: string;
  description?: string;
  isPackageRequired: boolean;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface InventoryCategoryCreateEntry {
  name: string;
  description?: string;
  isPackageRequired: boolean;
  isActive: boolean;
}

export interface InventoryCategoryListResponse {
  data: InventoryCategory[];
  total?: number;
}

class InventoryCategoriesService {
  private apiRoot = configs.apiRoot;

  async getInventoryCategories(params?: {
    page?: number;
    perPage?: number;
    filter?: any;
    sort?: any;
  }): Promise<InventoryCategory[]> {
    const queryParams = new URLSearchParams();
    
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.perPage) queryParams.append('size', params.perPage.toString());
    if (params?.filter) queryParams.append('filter', JSON.stringify(params.filter));
    if (params?.sort) queryParams.append('sort', JSON.stringify(params.sort));

    const url = `${this.apiRoot}/inventory_categories${
      queryParams.toString() ? `?${queryParams.toString()}` : ''
    }`;

    const response = await authenticatedFetch(url, { method: 'GET' });

    if (!response.ok) {
      const error = await response
        .json()
        .catch(() => ({ message: response.statusText }));
      throw new Error(
        error.message || `Failed to fetch inventory categories: ${response.statusText}`
      );
    }

    const result = await response.json();
    return Array.isArray(result) ? result : result.data || [];
  }

  async getInventoryCategory(id: string): Promise<InventoryCategory> {
    const response = await authenticatedFetch(
      `${this.apiRoot}/inventory_categories/${id}`,
      { method: 'GET' }
    );

    if (!response.ok) {
      const error = await response
        .json()
        .catch(() => ({ message: response.statusText }));
      throw new Error(
        error.message || `Failed to fetch inventory category: ${response.statusText}`
      );
    }

    return await response.json();
  }

  async createInventoryCategory(data: InventoryCategoryCreateEntry): Promise<InventoryCategory> {
    const response = await authenticatedFetch(
      `${this.apiRoot}/inventory_categories`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    );

    if (!response.ok) {
      const error = await response
        .json()
        .catch(() => ({ message: response.statusText }));
      throw new Error(
        error.message || `Failed to create inventory category: ${response.statusText}`
      );
    }

    return await response.json();
  }

  async updateInventoryCategory(
    id: string,
    data: Partial<InventoryCategoryCreateEntry>
  ): Promise<InventoryCategory> {
    const response = await authenticatedFetch(
      `${this.apiRoot}/inventory_categories/${id}`,
      {
        method: 'PUT',
        body: JSON.stringify(data),
      }
    );

    if (!response.ok) {
      const error = await response
        .json()
        .catch(() => ({ message: response.statusText }));
      throw new Error(
        error.message || `Failed to update inventory category: ${response.statusText}`
      );
    }

    return await response.json();
  }

  async deleteInventoryCategory(id: string): Promise<void> {
    const response = await authenticatedFetch(
      `${this.apiRoot}/inventory_categories/${id}`,
      { method: 'DELETE' }
    );

    if (!response.ok) {
      const error = await response
        .json()
        .catch(() => ({ message: response.statusText }));
      throw new Error(
        error.message || `Failed to delete inventory category: ${response.statusText}`
      );
    }
  }
}

export const inventoryCategoriesService = new InventoryCategoriesService();

